import { arrowHead } from '../model/arrow';
import { buildArrowCellMap, cellIndex, inBounds, type BoardData } from '../model/board';
import { DIRECTION_VECTORS } from '../model/direction';
import { BoardIndex } from '../rules/boardIndex';

/**
 * Dependency analysis on the untouched board.
 *
 * Arrow A depends on arrow B when B occupies a cell of A's exit corridor. Only the nearest
 * blocker decides immediate legality, so components and depth come from nearest-blocker
 * edges, while the total edge count walks the whole corridor — a measure of how constrained
 * the board is overall.
 */
export interface DependencyGraph {
  /** Nearest blocking arrow per arrow id, -1 when the arrow starts legal. */
  readonly nearestBlocker: Int32Array;
  /** Distinct (A, B) pairs where B sits anywhere in A's exit corridor. */
  readonly edgeCount: number;
  /** Arrow ids that are legal on the untouched board. */
  readonly roots: number[];
  /** Component id per arrow, from connectivity over nearest-blocker edges. */
  readonly componentOf: Int32Array;
  readonly componentSizes: number[];
  /** How many arrows point directly at each arrow. */
  readonly inDegree: Int32Array;
}

export function buildDependencyGraph(board: BoardData): DependencyGraph {
  const count = board.arrows.length;
  const index = BoardIndex.fromBoard(board);
  const nearestBlocker = new Int32Array(count).fill(-1);
  const inDegree = new Int32Array(count);
  const roots: number[] = [];

  for (let id = 0; id < count; id += 1) {
    const blocker = index.nearestBlocker(id);
    nearestBlocker[id] = blocker;
    if (blocker < 0) roots.push(id);
    else inDegree[blocker] += 1;
  }

  return {
    nearestBlocker,
    edgeCount: countCorridorEdges(board),
    roots,
    ...connectedComponents(count, nearestBlocker),
    inDegree,
  };
}

/**
 * Walks each arrow's exit corridor and counts the distinct arrows standing in it.
 * Linear in board area rather than in arrow pairs.
 */
function countCorridorEdges(board: BoardData): number {
  const map = buildArrowCellMap(board);
  let edges = 0;

  for (const arrow of board.arrows) {
    const head = arrowHead(arrow);
    const { dx, dy } = DIRECTION_VECTORS[arrow.direction];
    const seen = new Set<number>();

    let x = head.x + dx;
    let y = head.y + dy;
    while (inBounds(board, x, y)) {
      const owner = map[cellIndex(board, x, y)];
      if (owner >= 0 && owner !== arrow.id) seen.add(owner);
      x += dx;
      y += dy;
    }
    edges += seen.size;
  }
  return edges;
}

function connectedComponents(
  count: number,
  nearestBlocker: Int32Array,
): { componentOf: Int32Array; componentSizes: number[] } {
  const parent = new Int32Array(count);
  for (let i = 0; i < count; i += 1) parent[i] = i;

  const find = (node: number): number => {
    let root = node;
    while (parent[root] !== root) root = parent[root];
    let current = node;
    while (parent[current] !== root) {
      const next = parent[current];
      parent[current] = root;
      current = next;
    }
    return root;
  };

  for (let id = 0; id < count; id += 1) {
    const blocker = nearestBlocker[id];
    if (blocker < 0 || blocker === id) continue;
    const a = find(id);
    const b = find(blocker);
    if (a !== b) parent[a > b ? a : b] = a > b ? b : a;
  }

  // Relabel in ascending representative order so component ids are deterministic.
  const labels = new Map<number, number>();
  const componentOf = new Int32Array(count).fill(-1);
  const componentSizes: number[] = [];
  for (let id = 0; id < count; id += 1) {
    const root = find(id);
    let label = labels.get(root);
    if (label === undefined) {
      label = componentSizes.length;
      labels.set(root, label);
      componentSizes.push(0);
    }
    componentOf[id] = label;
    componentSizes[label] += 1;
  }
  return { componentOf, componentSizes };
}
